// Shared draft state for the Studio opener editor + phone preview + save.
// Single source of truth so the editor panel, the phone-mockup preview,
// and the save action never drift.

import React, { createContext, useCallback, useContext, useMemo, useState } from 'react';
import { getPreset, PRESET_LIST } from '../../openers/openerRegistry';

const OpenerDraftCtx = createContext(null);

export function OpenerDraftProvider({ initialOpener, onSave, children }) {
  // Phase 1.9.16 — preset-agnostic default:
  //   • existing event with a saved opener → use its presetId verbatim
  //   • blank event → visually preselect PRESET_LIST[0] for convenience,
  //     but do NOT persist anything until the user explicitly enables/saves.
  //     `enabled` stays false until then, so the empty-state UX gates the
  //     actual write. No hardcoded Rain ID anywhere.
  const initialPresetId = initialOpener?.presetId || PRESET_LIST[0]?.id || null;
  const initialPreset = initialPresetId ? getPreset(initialPresetId) : null;
  const initialMerged = { ...(initialPreset?.defaults || {}), ...(initialOpener?.settings || {}) };
  // Phase 1.9.1 — explicit enabled flag. Legacy saved opener docs without
  // `enabled` are treated as enabled (so existing configured events keep
  // their opener until the designer explicitly toggles it off).
  const initialEnabled = initialOpener?.presetId
    ? (initialOpener.enabled !== false)
    : false;

  const [presetId, setPresetId] = useState(initialPresetId);
  const [draftSettings, setDraftSettings] = useState(initialMerged);
  const [savedSettings, setSavedSettings] = useState(initialOpener?.settings || initialPreset?.defaults || {});
  const [replayKey, setReplayKey] = useState(0);
  const [enabled, setEnabledState] = useState(initialEnabled);
  const [savedEnabled, setSavedEnabled] = useState(initialEnabled);
  // True once the event has any saved opener doc (presetId persisted).
  const [hasSavedOpener, setHasSavedOpener] = useState(!!initialOpener?.presetId);

  const isDirty = useMemo(() => (
    JSON.stringify(draftSettings) !== JSON.stringify(savedSettings) ||
    enabled !== savedEnabled
  ), [draftSettings, savedSettings, enabled, savedEnabled]);

  // Deep-path setter so schema controls can use "openingTypography.color" etc.
  const setDraftSetting = useCallback((path, value) => {
    setDraftSettings((prev) => {
      const next = { ...prev };
      const keys = String(path).split('.');
      let cur = next;
      for (let i = 0; i < keys.length - 1; i++) {
        cur[keys[i]] = { ...(cur[keys[i]] || {}) };
        cur = cur[keys[i]];
      }
      cur[keys[keys.length - 1]] = value;
      return next;
    });
  }, []);

  const applyCombo = useCallback((combo) => {
    if (!combo?.settings) return;
    setDraftSettings((prev) => ({ ...prev, ...combo.settings }));
  }, []);

  const resetToDefaults = useCallback(() => {
    const p = getPreset(presetId);
    if (p) setDraftSettings({ ...p.defaults });
  }, [presetId]);

  // Phase 1.9.16 — preset switcher. Atomically swaps the active preset
  // and resets draft settings to that preset's defaults. We do NOT merge
  // across presets in v1: each preset owns its own schema and key shape,
  // so a Rain `flyDirection` would be meaningless in Letterpress.
  // The caller (editor panel) is responsible for confirming unsaved
  // changes BEFORE invoking this — we don't gate at the context layer
  // because the panel has the dirty-state UX.
  const switchPreset = useCallback((nextPresetId) => {
    const next = getPreset(nextPresetId);
    if (!next) return;
    setPresetId(nextPresetId);
    setDraftSettings({ ...next.defaults });
    // Bump the replay key so the phone preview remounts cleanly with
    // the new preset's animation.
    setReplayKey((k) => k + 1);
  }, []);

  const replay = useCallback(() => setReplayKey((k) => k + 1), []);

  // Phase 1.9.1 — explicit enable/disable. Persists to backend with same
  // PATCH /opener call so guests immediately stop/start seeing the opener
  // without needing a separate "Save" click.
  const setEnabled = useCallback(async (next) => {
    setEnabledState(!!next);
    if (!onSave) return;
    const p = getPreset(presetId);
    if (!p) return;
    await onSave({ enabled: !!next, presetId, version: p.version, settings: draftSettings });
    setSavedSettings(draftSettings);
    setSavedEnabled(!!next);
    setHasSavedOpener(true);
  }, [onSave, presetId, draftSettings]);

  const save = useCallback(async () => {
    const p = getPreset(presetId);
    if (!onSave || !p) return;
    await onSave({ enabled, presetId, version: p.version, settings: draftSettings });
    setSavedSettings(draftSettings);
    setSavedEnabled(enabled);
    setHasSavedOpener(true);
  }, [draftSettings, presetId, enabled, onSave]);

  const remove = useCallback(async () => {
    if (!onSave) return;
    await onSave(null);
    setEnabledState(false);
    setSavedEnabled(false);
    setHasSavedOpener(false);
  }, [onSave]);

  const value = useMemo(() => ({
    presetId,
    setPresetId,
    switchPreset,
    draftSettings,
    savedSettings,
    setDraftSetting,
    applyCombo,
    resetToDefaults,
    replay,
    replayKey,
    save,
    remove,
    enabled,
    setEnabled,
    hasSavedOpener,
    isDirty,
  }), [presetId, switchPreset, draftSettings, savedSettings, setDraftSetting, applyCombo, resetToDefaults, replay, replayKey, save, remove, enabled, setEnabled, hasSavedOpener, isDirty]);

  return <OpenerDraftCtx.Provider value={value}>{children}</OpenerDraftCtx.Provider>;
}

export function useOpenerDraft() {
  return useContext(OpenerDraftCtx);
}
